/**
 * Country Selector Component
 * With automatic geolocation detection
 */

import { useState, useRef, useEffect } from 'react';
import { useCountryStore } from '@schoolidb/store';
import { SUPPORTED_COUNTRIES, isCountrySupported } from '@schoolidb/config';

const countryFlags: Record<string, string> = {
  GLOBAL: '🌍',
  IN: '🇮🇳',
  US: '🇺🇸',
  GB: '🇬🇧',
  AE: '🇦🇪',
  SG: '🇸🇬',
  AU: '🇦🇺',
  CA: '🇨🇦',
};

// Map of country names to country codes for reverse geocoding
const countryNameToCode: Record<string, string> = {
  'india': 'IN',
  'united states': 'US',
  'usa': 'US',
  'united kingdom': 'GB',
  'uk': 'GB',
  'great britain': 'GB',
  'united arab emirates': 'AE',
  'uae': 'AE',
  'singapore': 'SG',
  'australia': 'AU',
  'canada': 'CA',
};

export function CountrySelector() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const countryCode = useCountryStore((state) => state.countryCode);
  const setCountry = useCountryStore((state) => state.setCountry);
  const setDetectedCountry = useCountryStore((state) => state.setDetectedCountry);
  const detectionSource = useCountryStore((state) => state.detectionSource);
  const isDetecting = useCountryStore((state) => state.isDetecting);
  const startDetection = useCountryStore((state) => state.startDetection);
  const endDetection = useCountryStore((state) => state.endDetection);

  // Detect location on mount (only if not manually set)
  useEffect(() => {
    if (detectionSource === 'manual') return;
    
    const detectLocation = async () => {
      startDetection();
      
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              // Use reverse geocoding to get country
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=3`
              );
              const data = await response.json();
              const country = data.address?.country?.toLowerCase();
              
              if (country && countryNameToCode[country]) {
                setDetectedCountry(countryNameToCode[country] as any, 'geolocation');
              } else if (country && isCountrySupported(country.toUpperCase())) {
                setDetectedCountry(country.toUpperCase() as any, 'geolocation');
              } else {
                // Country not supported, set to Global
                setDetectedCountry('GLOBAL' as any, 'geolocation');
              }
            } catch {
              // Fallback to Global on error
              setDetectedCountry('GLOBAL' as any, 'geolocation');
            }
          },
          () => {
            // Geolocation denied or failed, set to Global
            setDetectedCountry('GLOBAL' as any, 'default');
          },
          { timeout: 10000, maximumAge: 86400000 } // Cache for 24 hours
        );
      } else {
        endDetection();
      }
    };

    detectLocation();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentCountry = SUPPORTED_COUNTRIES.find((c) => c.code === countryCode);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Select country"
        disabled={isDetecting}
      >
        {isDetecting ? (
          <span className="text-sm text-gray-500">Detecting...</span>
        ) : (
          <>
            <span className="text-lg">{countryFlags[countryCode] || '🌍'}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:inline">
              {currentCountry?.name || 'Global'}
            </span>
            <span className="text-xs text-gray-400">▼</span>
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-2 z-50">
          {SUPPORTED_COUNTRIES.map((country) => (
            <button
              key={country.code}
              onClick={() => {
                setCountry(country.code as any);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                country.code === countryCode ? 'bg-primary-50 dark:bg-primary-900/20' : ''
              }`}
            >
              <span className="text-lg">{countryFlags[country.code] || '🌍'}</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">{country.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
